import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { BaseModalType, useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { currencyToSymbol } from '@utils/currency'
import { TradingAccountRestrictions } from '@utils/trading-account.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { NameDto } from '@model/NameDto'
import { TradingAccount } from '@model/TradingAccount'
import { CurrencyType, WalletDto } from '@model/WalletDto'

import { createFormField } from '@global/formField/FormField'
import { createFormNumericField } from '@global/formField/FormNumericField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { useDispatchOpenChat } from '@hooks/useDispatchOpenChat'
import { useFormatNumber } from '@hooks/useFormatNumber'
import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { DropdownInput } from '@shared/components/Dropdown'
import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'
import { ShortRiskWarningModal } from '@shared/modals/ShortRiskWarning.modal'

import { getRestrictionMessage } from './Balance'

import styles from './TransferForm.module.scss'

const FormNumericField = createFormNumericField()
type DisplayType = 'wallet' | 'tradingAccount'

interface WalletOrTradingAccountOption {
  id: string
  name: string
  currency: CurrencyType
  type: string
  balance: number | null
  displayType: DisplayType

  restrictions: NameDto[]
}

enum TransferFromType {
  Wallet = 'wallet',
  TA = 'tradingAccount',
}

interface MatrixOption {
  wallet: WalletOrTradingAccountOption
  tradingAccount: WalletOrTradingAccountOption
}

const getMatrixOptions = (wallets: WalletDto[], tradingAccounts: TradingAccount[]) => {
  const { id, name, platformOverview, restrictions } = tradingAccounts?.[0] || {}

  return wallets.reduce<Partial<Record<CurrencyType, MatrixOption>>>((options, wallet) => {
    const balanceObj = platformOverview?.balances?.find((b) => b.currency === wallet.currency.id)

    const balance = balanceObj?.balance || null

    options[wallet.currency.id] = {
      wallet: {
        id: wallet.id,
        name: wallet.name,
        currency: wallet.currency.id,
        balance: wallet.balance,
        type: t('Wallet.Wallet'),
        displayType: 'wallet',
        restrictions: wallet.restrictions,
      },
      tradingAccount: {
        id,
        name,
        currency: wallet.currency.id,
        balance,
        type: t('Trading Account.Trading Account'),
        displayType: 'tradingAccount',
        restrictions,
      },
    }

    return options
  }, {})
}

export interface TransferValues {
  transferFrom: WalletOrTradingAccountOption | null
  transferTo: WalletOrTradingAccountOption | null
  fromAmount: number | undefined
}

interface OuterProps {
  wallets: WalletDto[]
  currency?: CurrencyType
  fetchingAccounts: boolean
  tradingAccounts: TradingAccount[]
  preselectedWallet?: WalletDto
  preselectedTradingAccount?: TradingAccount
  handleFormSubmit: (values: TransferValues) => void
}

const FormField = createFormField<TransferValues>()

type TransferFormProps = FormikProps<TransferValues> & OuterProps

interface TransferFromSelectModalProps {
  matrixOptions: Partial<Record<CurrencyType, MatrixOption>>
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<TransferValues>>
  onSupportClick: () => void
  isSupportLoading: boolean
}

export const TransferFromSelectModal: BaseModalType<TransferFromSelectModalProps> = ({
  close,
  matrixOptions,
  setFieldValue,
  onSupportClick,
  isSupportLoading,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const handleOption = async (option?: WalletOrTradingAccountOption) => {
    if (!option) return
    await setFieldValue('transferFrom', option)
    const counterpart =
      option.displayType === 'wallet'
        ? matrixOptions[option.currency]?.tradingAccount
        : matrixOptions[option.currency]?.wallet
    await setFieldValue('transferTo', counterpart ?? null)
    close()
  }

  const entries = Object.entries(matrixOptions)

  return (
    <Modal>
      <Modal.Header>{t('Wallet.Transfer From')}</Modal.Header>
      <Modal.Body>
        {entries.map(([currency, pair]) => {
          if (!pair) return null
          const items: WalletOrTradingAccountOption[] = [pair.wallet]
          if ((pair.tradingAccount.balance ?? -1) >= 0) items.push(pair.tradingAccount)

          return (
            <React.Fragment key={currency}>
              <Modal.Body.Title>{currency}</Modal.Body.Title>
              {items.length > 0 && (
                <Modal.Body.Input.Radio
                  name={`transfer-from-${currency}`}
                  options={items.map((x) => ({
                    label: `${x.currency} ${x.type}`,
                    value: x.id,
                    disabled: isRestricted(WalletRestrictions.TRANSFER_FROM, x.restrictions),
                    caption: (
                      <>
                        {`${t('Trading Account.Balance')} ${formatMoney(
                          x.balance ?? 0,
                          x.currency
                        )}`}
                        {isRestricted(WalletRestrictions.TRANSFER_FROM, x.restrictions) ? (
                          <>
                            <Body1 className={styles.primaryText} isParagraph>
                              {getRestrictionMessage(true, false, x.displayType === 'wallet', t)}
                            </Body1>
                            <div>
                              {t('Please')}{' '}
                              <Link
                                variant='interactive'
                                disabled={isSupportLoading}
                                onClick={onSupportClick}
                              >
                                {t('Contact Support').toLowerCase()}
                              </Link>{' '}
                              {t('for more info.')}
                            </div>
                          </>
                        ) : null}
                      </>
                    ),
                  }))}
                  onChange={(id: string) => handleOption(items.find((y) => y.id === id))}
                />
              )}
            </React.Fragment>
          )
        })}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}

const TransferFormETDUI: React.FC<TransferFormProps> = (props) => {
  const {
    wallets,
    tradingAccounts,
    values,
    isValid,
    setFieldValue,
    handleSubmit,
    fetchingAccounts,
  } = props
  useScrollToTop()
  useOnLocaleSwitch(() => props.validateForm())

  const { transferFrom } = values

  const showTANotice = useMemo(() => {
    if (!tradingAccounts.length) {
      return false
    }
    if (values.transferFrom?.displayType !== 'wallet') {
      return false
    }
    return !tradingAccounts.some(({ platformOverview }) =>
      platformOverview.balances?.some((b) => b.currency === transferFrom?.currency)
    )
  }, [tradingAccounts, transferFrom?.currency, values.transferFrom?.displayType])

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { navigateToPreviousPath } = usePathHistoryContext()
  const cancelModal = useModal(ActionModal, {
    onConfirm: () => navigateToPreviousPath({ state: { wallets: true } }),
  })
  const { dispatchOpenChat, isLoading } = useDispatchOpenChat()

  const matrixOptions = useMemo<Partial<Record<CurrencyType, MatrixOption>>>(
    () => getMatrixOptions(wallets, tradingAccounts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wallets, tradingAccounts, t]
  )

  const transferFromSelectModal = useModal(TransferFromSelectModal, {
    matrixOptions,
    setFieldValue,
    onSupportClick: dispatchOpenChat,
    isSupportLoading: isLoading,
  })

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  const hasTransferToRestriction = values.transferTo?.restrictions?.some(
    (restriction: NameDto) => restriction.id === TradingAccountRestrictions.TRANSFER_TO
  )

  const riskWarningModal = useModal(ShortRiskWarningModal)

  return (
    <React.Fragment>
      <FormTemplate
        title={t('Wallet.Transfer')}
        goBack={cancelModal.open}
        renderIcon={
          <IconButton.Ghost
            icon={{ icon: WarningIcon, color: 'text-warning-high-em' }}
            onClick={() => riskWarningModal.open()}
          />
        }
      >
        <Form className={styles.wrapper} onSubmit={handleSubmitForm} autoComplete='off'>
          <Body1 className={classNames(styles.info)}>
            <span className='danger'>{t('Trading Account.Important')}</span>{' '}
            {t(
              'Transactions.You can only transfer between Wallets and Trading Accounts of the same currency'
            )}
          </Body1>
          <Loading showLoadingIcon isLoading={fetchingAccounts}>
            <DropdownInput
              name='transferFrom.name'
              label={t('Wallet.Transfer From')}
              value={
                values.transferFrom
                  ? `${values.transferFrom.currency} ${values.transferFrom.type}`
                  : ''
              }
              hint={
                values.transferFrom &&
                `${t('Trading Account.Balance')} ${formatMoney(
                  values.transferFrom.balance ?? 0,
                  values.transferFrom.currency
                )}`
              }
              required
              onClick={() => transferFromSelectModal.open()}
            />
            <div className={styles.amountWrapper}>
              <div className={styles.first}>
                <FormNumericField
                  name='fromAmount'
                  label={t('Wallet.Sending Amount')}
                  placeholder={t('Wallet.Sending Amount')}
                  decimalPlaces={2}
                  disabled={!transferFrom?.id}
                  required
                  step='any'
                  autoComplete='false'
                />
              </div>
              <div className={styles.last}>
                <FormField
                  name='transferFrom.currency'
                  label={t('Wallet.Currency')}
                  className={styles.last}
                  value={transferFrom && transferFrom.currency}
                  disabled
                />
              </div>
            </div>
            <div>
              <DropdownInput
                disabled
                name={'transferTo.id'}
                label={t('Wallet.Transfer To')}
                value={
                  values.transferTo ? `${values.transferTo.currency} ${values.transferTo.type}` : ''
                }
                hint={
                  values.transferTo &&
                  `${t('Trading Account.Balance')} ${formatMoney(
                    values.transferTo.balance ?? 0,
                    values.transferTo.currency
                  )}`
                }
                readOnly
                required
              />
              {hasTransferToRestriction && (
                <div>
                  <Body2 className={styles.primaryText} isParagraph>
                    {t('Transfer restricted to.TM Trading Account')}
                  </Body2>
                  <Body2>
                    {t('Please')}{' '}
                    <Link disabled={isLoading} onClick={dispatchOpenChat}>
                      {t('Contact Support').toLowerCase()}
                    </Link>{' '}
                    {t('for more info.')}
                  </Body2>
                </div>
              )}
            </div>
          </Loading>
          {showTANotice && (
            <div className={styles.walletNotice}>
              <div className={styles.symbolWrapper}>
                <Caption1 className={styles.symbol} medium>
                  {currencyToSymbol(values.transferTo?.currency)}
                </Caption1>
              </div>
              <Body2>
                {t('Wallet.A new currency balance will be added to your Trading Account', {
                  currency: values.transferTo?.currency,
                })}
              </Body2>
            </div>
          )}
          <TransactionFormActionButtonTemplate>
            <Button.Secondary onClick={() => cancelModal.open()} size='lg'>
              {t('Cancel')}
            </Button.Secondary>
            <Button.Primary
              size='lg'
              type='submit'
              fullWidth
              disabled={!isValid || hasTransferToRestriction}
            >
              {t('Confirm')}
            </Button.Primary>
          </TransactionFormActionButtonTemplate>
        </Form>
      </FormTemplate>
    </React.Fragment>
  )
}

export const TransferFormETD = withFormik<OuterProps, TransferValues>({
  mapPropsToValues: (props) => {
    const { preselectedWallet, preselectedTradingAccount, wallets, currency, tradingAccounts } =
      props

    const transferFrom = preselectedWallet
      ? {
          id: preselectedWallet.id,
          currency: preselectedWallet.currency.id,
          balance: preselectedWallet.balance,
          name: preselectedWallet.name,
          type: t('Wallet.Wallet'),
          displayType: TransferFromType.Wallet,
          restrictions: preselectedWallet.restrictions,
        }
      : preselectedTradingAccount &&
        currency &&
        (preselectedTradingAccount?.platformOverview.balances.find(
          (balance) => balance.currency === currency
        )?.balance ?? -1) >= 0
      ? {
          id: preselectedTradingAccount.id,
          currency: currency,
          balance:
            preselectedTradingAccount.platformOverview.balances.find(
              (balance) => balance.currency === currency
            )?.balance || 0,
          name: preselectedTradingAccount.name,
          type: t('Trading Account.Trading Account'),
          displayType: TransferFromType.TA,
          restrictions: preselectedTradingAccount.restrictions,
        }
      : null

    const matrixOptions = getMatrixOptions(wallets, tradingAccounts)
    const transferTo = transferFrom
      ? transferFrom.displayType === 'wallet'
        ? matrixOptions[transferFrom.currency]?.tradingAccount || null
        : matrixOptions[transferFrom.currency]?.wallet || null
      : null

    return {
      transferFrom,
      transferTo,
      fromAmount: undefined,
    }
  },

  handleSubmit: async (values, { props }) => {
    props.handleFormSubmit(values)
  },
  validate: (values) => {
    const errors: FormikErrors<TransferValues> = {}
    if (!values.transferFrom) {
      errors.transferFrom = t('Validation.Required')
    }

    if (isValidMinAmount(values)) {
      errors.fromAmount = t('Validation.Amount should be greater than 0.00')
    }

    if (isValidMaxAmount(values)) {
      if (isTransferFromWallet(values)) {
        errors.fromAmount = t('Validation.There is not enough balance in your wallet')
      }
      if (isTransferFromTA(values)) {
        errors.fromAmount = t('Validation.There is not enough balance in your trading account')
      }
    }

    if (!values.transferTo) {
      errors.transferTo = t('Validation.Required')
    }
    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(TransferFormETDUI)

const isTransferFromWallet = (values: TransferValues) => {
  return values.transferFrom?.displayType === TransferFromType.Wallet
}

const isTransferFromTA = (values: TransferValues) => {
  return values.transferFrom?.displayType === TransferFromType.TA
}

const isValidMinAmount = (values: TransferValues): boolean => {
  const amount = values?.fromAmount ?? 0
  return amount <= 0
}

const isValidMaxAmount = (values: TransferValues): boolean => {
  const maxAmount = getBalance(values)
  const amount = values?.fromAmount ?? 0
  return amount > maxAmount
}

const getBalance = (values: TransferValues) => {
  return values?.transferFrom?.balance ?? 0
}
